import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

/**
 * Reemplaza localhost por 10.0.2.2 (IP especial del emulador Android para acceder al host)
 * @param {string} url - URL original
 * @returns {string} URL con localhost reemplazado
 */
function replaceLocalhostForEmulator(url) {
  if (!url) return url;
  
  // En Android emulator, 10.0.2.2 es la IP especial para acceder al localhost de la máquina host
  // Reemplazar localhost por 10.0.2.2 en el host de la URL principal
  return url
    .replace(/http:\/\/localhost:(\d+)/g, 'http://10.0.2.2:$1')
    .replace(/https:\/\/localhost:(\d+)/g, 'https://10.0.2.2:$1');
}

/**
 * Reemplaza el redirect_uri en la URL para usar el esquema personalizado de la app móvil
 * @param {string} url - URL original con redirect_uri
 * @returns {string} URL con redirect_uri modificado para móvil
 */
function replaceRedirectUriForMobile(url) {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    
    // ESTRATEGIA: Eliminar COMPLETAMENTE redirect_uri de la URL que se envía al backend
    // El backend debe detectar mobile=true y usar el esquema correcto automáticamente
    // Esto evita que el backend agregue su propio redirect_uri causando duplicados
    
    // Eliminar TODAS las instancias de redirect_uri
    urlObj.searchParams.delete('redirect_uri');
    
    // Eliminar mobile si existe
    urlObj.searchParams.delete('mobile');
    
    // SOLO agregar mobile=true
    // NO agregar redirect_uri aquí - el backend debe detectar mobile=true y usar el esquema correcto
    urlObj.searchParams.set('mobile', 'true');
    
    const finalUrl = urlObj.toString();
    
    // Validación: asegurarse de que NO haya redirect_uri en la URL final
    const redirectUriMatches = finalUrl.match(/redirect_uri=/gi);
    if (redirectUriMatches && redirectUriMatches.length > 0) {
      console.warn('ADVERTENCIA: redirect_uri todavía presente después de limpiar, usando regex...');
      return cleanUrlWithRegex(url, false); // false = no agregar redirect_uri
    }
    
    console.log('URL limpia (sin redirect_uri, solo mobile=true):', finalUrl);
    return finalUrl;
  } catch (error) {
    console.error('Error procesando URL:', error);
    // Si falla el parsing, usar regex para eliminar duplicados
    return cleanUrlWithRegex(url, false); // false = no agregar redirect_uri
  }
}

/**
 * Limpia la URL usando regex para eliminar duplicados
 * @param {string} url - URL a limpiar
 * @param {boolean} includeRedirectUri - Si true, agrega redirect_uri. Si false, solo agrega mobile=true
 * @returns {string} URL limpia sin duplicados
 */
function cleanUrlWithRegex(url, includeRedirectUri = false) {
  let modifiedUrl = url;
  
  // Separar la URL base de los parámetros
  const urlParts = modifiedUrl.split('?');
  const baseUrl = urlParts[0];
  const queryString = urlParts[1] || '';
  
  // Crear un objeto para rastrear parámetros únicos
  const params = new Map();
  
  // Parsear todos los parámetros existentes (excluyendo redirect_uri y mobile)
  if (queryString) {
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key && key !== 'redirect_uri' && key !== 'mobile') {
        params.set(key, value || '');
      }
    });
  }
  
  // Solo agregar mobile=true
  params.set('mobile', 'true');
  
  // Solo agregar redirect_uri si se solicita explícitamente
  // Por defecto NO lo agregamos para que el backend lo maneje basado en mobile=true
  if (includeRedirectUri) {
    params.set('redirect_uri', 'com.mobius.app://oauth2/redirect');
  }
  
  // Reconstruir la URL
  const newQueryString = Array.from(params.entries())
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  
  return `${baseUrl}?${newQueryString}`;
}

/**
 * Agrega el parámetro mobile=true a la URL si está en móvil
 * @param {string} url - URL original
 * @returns {string} URL con el parámetro mobile agregado
 */
function addMobileParameter(url) {
  if (!url) return url;
  
  // Verificar si la URL ya tiene parámetros
  const hasParams = url.includes('?');
  const separator = hasParams ? '&' : '?';
  
  // Agregar mobile=true
  return `${url}${separator}mobile=true`;
}

/**
 * Construye una URL limpia desde cero para evitar duplicados
 * @param {string} originalUrl - URL original del backend
 * @returns {string} URL limpia construida desde cero
 */
function buildCleanMobileUrl(originalUrl) {
  try {
    const urlObj = new URL(originalUrl);
    
    // Obtener solo la URL base (sin parámetros)
    const baseUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
    
    // Reemplazar localhost por 10.0.2.2 en la URL base
    const mobileBaseUrl = baseUrl
      .replace(/http:\/\/localhost:(\d+)/g, 'http://10.0.2.2:$1')
      .replace(/https:\/\/localhost:(\d+)/g, 'https://10.0.2.2:$1');
    
    // Construir URL completamente nueva con solo mobile=true
    // NO incluir redirect_uri - el backend debe detectar mobile=true y usarlo
    const cleanUrl = `${mobileBaseUrl}?mobile=true`;
    
    console.log('=== DEBUG OAUTH MÓVIL ===');
    console.log('URL original:', originalUrl);
    console.log('URL base extraída:', baseUrl);
    console.log('URL base móvil:', mobileBaseUrl);
    console.log('URL final limpia:', cleanUrl);
    console.log('Parámetros en URL final:', new URL(cleanUrl).searchParams.toString());
    console.log('========================');
    
    return cleanUrl;
  } catch (error) {
    console.error('Error construyendo URL limpia:', error);
    // Fallback: usar el método anterior
    let mobileAuthUrl = replaceLocalhostForEmulator(originalUrl);
    mobileAuthUrl = replaceRedirectUriForMobile(mobileAuthUrl);
    return mobileAuthUrl;
  }
}

/**
 * Maneja el login de Google de forma compatible con web y móvil
 * @param {string} authUrl - URL de autenticación de Google
 */
export async function handleGoogleLogin(authUrl) {
  // Si está corriendo en Capacitor (móvil), usar el plugin Browser y construir URL limpia
  if (Capacitor.isNativePlatform()) {
    try {
      // Construir URL completamente nueva desde cero para evitar duplicados
      const mobileAuthUrl = buildCleanMobileUrl(authUrl);
      
      // Validación final: verificar que no haya redirect_uri en la URL
      const hasRedirectUri = mobileAuthUrl.includes('redirect_uri');
      if (hasRedirectUri) {
        console.error('ERROR: redirect_uri todavía presente en URL final!');
        console.error('URL:', mobileAuthUrl);
        // Intentar limpiar de nuevo
        const cleanedUrl = mobileAuthUrl.split('?')[0] + '?mobile=true';
        console.log('URL limpiada manualmente:', cleanedUrl);
        await Browser.open({
          url: cleanedUrl,
          windowName: '_system'
        });
        return;
      }
      
      // Abrir OAuth en el navegador del sistema
      await Browser.open({
        url: mobileAuthUrl,
        windowName: '_system'
      });
      
      // El redirect automático debería funcionar gracias al intent-filter en AndroidManifest.xml
      // El esquema com.mobius.app://oauth2/redirect será capturado por la app
    } catch (error) {
      console.error('Error abriendo OAuth:', error);
      // Fallback: construir URL limpia manualmente
      try {
        const urlObj = new URL(authUrl);
        const baseUrl = `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
        const mobileBaseUrl = baseUrl.replace(/localhost/g, '10.0.2.2');
        const fallbackUrl = `${mobileBaseUrl}?mobile=true`;
        console.log('Usando URL fallback:', fallbackUrl);
        window.location.href = fallbackUrl;
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError);
        window.location.href = authUrl;
      }
    }
  } else {
    // En web, usar el método normal sin modificaciones
    window.location.href = authUrl;
  }
}
